import { useMutation } from '@apollo/client';
import { useState } from 'react';

import { useCompleteFragment } from '@eluve/apollo-client';
import { SimpleTooltip } from '@eluve/components';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Icon,
  Input,
  useToast,
} from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';

interface RenameTenantActionProps {
  tenantId: string;
}

const tenantNameFragment = graphql(`
  fragment TenantName on AdminTenants {
    name
  }
`);

const renameTenantMutation = graphql(`
  mutation renameTenant($tenantId: uuid!, $name: String!) {
    updateAdminTenants(
      where: { id: { _eq: $tenantId } }
      _set: { name: $name }
    ) {
      returning {
        __typename
        id
        name
      }
    }
  }
`);

export const RenameTenantAction: React.FC<RenameTenantActionProps> = ({
  tenantId,
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { name: defaultTenantName } = useCompleteFragment({
    fragment: tenantNameFragment,
    key: { id: tenantId },
  });
  const toast = useToast();
  const [renameTenant] = useMutation(renameTenantMutation, {
    optimisticResponse: ({ name, tenantId }) => ({
      updateAdminTenants: {
        returning: [
          {
            __typename: 'AdminTenants' as const,
            id: tenantId,
            name,
          },
        ],
      },
    }),
    onError: () => {
      toast.error('Failed to rename organization');
      setIsDialogOpen(true);
    },
  });

  const initialTenantName = defaultTenantName ?? '';
  const [newTenantName, setNewTenantName] = useState(initialTenantName);

  const handleDialogOpen = (open: boolean) => {
    if (open) {
      setNewTenantName(initialTenantName);
      setIsDialogOpen(open);
      return;
    }

    if (
      newTenantName !== defaultTenantName &&
      !window.confirm('Are you sure you want to discard changes?')
    ) {
      return;
    }

    setIsDialogOpen(false);
  };

  const handleRenameTenant = () => {
    renameTenant({
      variables: {
        tenantId,
        name: newTenantName,
      },
    });

    setIsDialogOpen(false);
  };

  return (
    <Dialog open={isDialogOpen} onOpenChange={handleDialogOpen} modal>
      <DialogTrigger asChild>
        <Button variant="outline" aria-label="Rename organization">
          <SimpleTooltip label="Rename">
            <Icon name="Pencil" size="xl" />
          </SimpleTooltip>
        </Button>
      </DialogTrigger>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Rename Organization</DialogTitle>
        </DialogHeader>
        <Input
          placeholder="Enter new organization name"
          value={newTenantName ?? ''}
          onChange={(e) => setNewTenantName(e.target.value)}
        />
        <DialogFooter>
          <Button variant="outline" onClick={() => handleDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleRenameTenant}>Rename</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
